import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { LoadingSpinner } from "../ui";
import useAuth from "../../hooks/useAuth";
import { RootState } from "../../redux/store";
import { setPrevUrl } from "../../redux/auth/authSlice";

interface PrivateRouteProps {
  allowedRoles: string[];
}

const PrivateRoute = ({ allowedRoles }: PrivateRouteProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoading } = useAuth();
  const { isAuthenticated, lang, user } = useSelector(
    (state: RootState) => state.auth
  );

  const prevUrl = `${location.pathname}${location.search}`;

  useEffect(() => {
    // Only set prevUrl if it's not a role-based dashboard URL
    // This prevents issues when a user's role changes
    const isRoleDashboard =
      /\/(user|admin|super-admin|school-admin)(\/dashboard)?(\/.*)?$/.test(
        prevUrl
      );

    if (!isRoleDashboard) {
      dispatch(setPrevUrl(prevUrl));
    }
  }, [dispatch, prevUrl]);

  if (isLoading) {
    return <LoadingSpinner size="xxl" />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/${lang}/auth/login`}
        state={{ from: location, prevUrl }}
        replace
      />
    );
  }

  const allowed = allowedRoles.includes(user?.role || "");
  return allowed ? (
    <Outlet />
  ) : (
    <Navigate
      to={`/${lang}/auth/login`}
      state={{ from: location, prevUrl }}
      replace
    />
  );
};

export default PrivateRoute;
